import { Controller } from "@hotwired/stimulus"

// Contrôleur Stimulus pour la barre de filtres
// Gère la recherche avec debounce, l'auto-submit des filtres et le panneau mobile
export default class extends Controller {
  static targets = ["mobileFilters"]

  connect() {
    // Timer pour le debounce de la recherche
    this.searchTimeout = null

    // Configuration du délai de debounce (ms)
    this.debounceDelay = 500
  }

  disconnect() {
    // Nettoyer le timer si le contrôleur est déconnecté
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  // Submit immédiat du formulaire (pour les select)
  submit(event) {
    // Soumettre le formulaire parent
    const form = this.element

    // Utiliser requestSubmit pour déclencher les événements de soumission
    // et permettre à Turbo de gérer la requête
    if (form.requestSubmit) {
      form.requestSubmit()
    } else {
      // Fallback pour les navigateurs qui ne supportent pas requestSubmit
      form.submit()
    }
  }

  // Submit avec debounce pour la recherche
  debouncedSubmit(event) {
    // Annuler le timer précédent s'il existe
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    // Créer un nouveau timer
    this.searchTimeout = setTimeout(() => {
      this.submit(event)
    }, this.debounceDelay)
  }

  // Toggle du panneau de filtres mobile
  toggleMobileFilters(event) {
    event.preventDefault()

    if (this.hasMobileFiltersTarget) {
      const panel = this.mobileFiltersTarget

      // Toggle de la classe hidden
      if (panel.classList.contains("hidden")) {
        // Ouvrir le panneau
        panel.classList.remove("hidden")
        panel.classList.add("block")

        // Animation d'entrée (optionnel)
        panel.style.opacity = "0"
        panel.style.transform = "translateY(-10px)"

        requestAnimationFrame(() => {
          panel.style.transition = "opacity 0.2s, transform 0.2s"
          panel.style.opacity = "1"
          panel.style.transform = "translateY(0)"
        })

        // Mettre à jour le texte du bouton
        this.updateToggleButton(event.currentTarget, true)
      } else {
        // Fermer le panneau
        panel.style.transition = "opacity 0.2s, transform 0.2s"
        panel.style.opacity = "0"
        panel.style.transform = "translateY(-10px)"

        setTimeout(() => {
          panel.classList.add("hidden")
          panel.classList.remove("block")
        }, 200)

        // Mettre à jour le texte du bouton
        this.updateToggleButton(event.currentTarget, false)
      }
    }
  }

  // Met à jour l'apparence du bouton toggle
  updateToggleButton(button, isOpen) {
    const icon = button.querySelector("svg")

    if (isOpen) {
      // Rotation de l'icône quand ouvert
      if (icon) {
        icon.style.transform = "rotate(180deg)"
        icon.style.transition = "transform 0.2s"
      }

      // Ajouter une classe pour indiquer l'état actif
      button.classList.add("bg-gray-100")
      button.classList.remove("hover:bg-gray-50")
    } else {
      // Retour à l'état normal
      if (icon) {
        icon.style.transform = "rotate(0deg)"
      }

      button.classList.remove("bg-gray-100")
      button.classList.add("hover:bg-gray-50")
    }
  }

  // Synchroniser les valeurs entre les filtres desktop et mobile
  syncFilters(event) {
    const changedElement = event.target
    const name = changedElement.name
    const value = changedElement.value

    // Trouver l'élément correspondant (desktop ou mobile)
    const correspondingElement = this.element.querySelector(
      `[name="${name}"]:not([id^="mobile_"])` // Desktop
    ) || this.element.querySelector(
      `[name="${name}"][id^="mobile_"]` // Mobile
    )

    if (correspondingElement && correspondingElement !== changedElement) {
      correspondingElement.value = value
    }
  }

  // Réinitialiser tous les filtres
  clearFilters(event) {
    // Si c'est un lien, on laisse le comportement par défaut
    // Sinon, on réinitialise manuellement
    if (event.target.tagName !== "A") {
      event.preventDefault()

      // Réinitialiser tous les inputs
      this.element.querySelectorAll("input[type='text'], input[type='search']").forEach(input => {
        input.value = ""
      })

      // Réinitialiser tous les selects
      this.element.querySelectorAll("select").forEach(select => {
        select.selectedIndex = 0
      })

      // Soumettre le formulaire
      this.submit(event)
    }
  }

  // Gérer l'appui sur Entrée dans le champ de recherche
  handleSearchKeypress(event) {
    if (event.key === "Enter") {
      event.preventDefault()

      // Annuler le debounce et soumettre immédiatement
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.submit(event)
    }
  }

  // Méthode pour compter les filtres actifs
  countActiveFilters() {
    let count = 0

    // Compter les inputs non vides
    this.element.querySelectorAll("input[type='text'], input[type='search']").forEach(input => {
      if (input.value.trim() !== "") {
        count++
      }
    })

    // Compter les selects avec une valeur sélectionnée
    this.element.querySelectorAll("select").forEach(select => {
      if (select.value && select.value !== "") {
        count++
      }
    })

    return count
  }

  // Mettre à jour le badge du nombre de filtres
  updateFilterBadge() {
    const count = this.countActiveFilters()
    const badge = this.element.querySelector(".filter-count-badge")

    if (badge) {
      if (count > 0) {
        badge.textContent = count
        badge.classList.remove("hidden")
      } else {
        badge.classList.add("hidden")
      }
    }
  }
}